// filepath: apps/web/src/features/admin/hmacKeys/IssueKeyFab.tsx
//
// FR-UI-ADM-7 AC-11 -- Floating Action Button to issue the first HMAC key.
//
// When the keys list is empty, the page renders this FAB instead of
// the table. Clicking the FAB opens a confirm dialog before the actual
// POST /api/webhooks/keys. On 201 the parent opens the OneTimeCopyDialog.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIssueKey } from "./queries";
import { errorApiToMessage } from "./errorMessage";
import type { IssueKeyResponse } from "./types";

interface IssueKeyFabProps {
  onSuccess: (resp: IssueKeyResponse) => void;
  environmentKind: string;
}

export function IssueKeyFab({
  onSuccess,
  environmentKind,
}: IssueKeyFabProps): React.ReactElement {
  const { t } = useTranslation();
  const issue = useIssueKey();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (environmentKind !== "staging") {
    return <></>;
  }

  const handleConfirm = async () => {
    setError(null);
    try {
      const resp = await issue.mutateAsync();
      setConfirmOpen(false);
      onSuccess(resp);
    } catch (err) {
      setError(errorApiToMessage(err, t));
    }
  };

  return (
    <>
      <button
        type="button"
        className="hmac-keys-fab"
        onClick={() => setConfirmOpen(true)}
        data-testid="issue-key-fab"
      >
        {t("admin.webhooks.keys.issue_action")}
      </button>
      {confirmOpen && (
        <div
          data-testid="issue-key-confirm"
          className="hmac-keys-dialog"
          role="dialog"
          aria-modal="true"
        >
          <h2>{t("admin.webhooks.keys.issue_action")}</h2>
          <p>{t("admin.webhooks.keys.one_time_copy_warning")}</p>
          {error && (
            <p data-testid="issue-key-error" className="hmac-keys-error">
              {error}
            </p>
          )}
          <div className="hmac-keys-dialog__actions">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              data-testid="issue-key-cancel"
            >
              {t("admin.webhooks.keys.cancel_button")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={issue.isPending}
              data-testid="issue-key-submit"
            >
              {t("admin.webhooks.keys.issue_action")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
